import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Pencil, X, Save, Type, Palette, RotateCcw, Ruler } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const FONT_OPTIONS = [
  { value: "'Cabinet Grotesk', sans-serif", label: 'Cabinet Grotesk' },
  { value: "'IBM Plex Sans', sans-serif", label: 'IBM Plex Sans' },
  { value: "'Inter', sans-serif", label: 'Inter' },
  { value: "'DM Sans', sans-serif", label: 'DM Sans' },
  { value: "'Space Grotesk', sans-serif", label: 'Space Grotesk' },
  { value: "'Outfit', sans-serif", label: 'Outfit' },
  { value: "'Sora', sans-serif", label: 'Sora' },
  { value: "'Manrope', sans-serif", label: 'Manrope' },
  { value: "'Plus Jakarta Sans', sans-serif", label: 'Plus Jakarta Sans' },
  { value: "'Poppins', sans-serif", label: 'Poppins' },
];

const MONO_OPTIONS = [
  { value: "'IBM Plex Mono', monospace", label: 'IBM Plex Mono' },
  { value: "'JetBrains Mono', monospace", label: 'JetBrains Mono' },
  { value: "'Fira Code', monospace", label: 'Fira Code' },
  { value: "'Source Code Pro', monospace", label: 'Source Code Pro' },
];

const DEFAULT_SETTINGS = {
  primary_color: '#0055FF',
  primary_hover: '#0044CC',
  background_color: '#FAFAFA',
  surface_color: '#FFFFFF',
  text_primary: '#0A0A0A',
  text_secondary: '#525252',
  border_color: '#E5E5E5',
  accent_color: '#10B981',
  warning_color: '#F59E0B',
  danger_color: '#EF4444',
  brand_name: 'GetScribeDash',
  tagline: 'Turn handwriting into text in hours',
  hero_heading: 'Available Tasks',
  font_heading: "'Cabinet Grotesk', sans-serif",
  font_body: "'IBM Plex Sans', sans-serif",
  font_mono: "'IBM Plex Mono', monospace",
  font_size_base: 16,
};

// Load Google Fonts dynamically
function loadGoogleFont(fontFamily) {
  const name = fontFamily.split("'")[1];
  if (!name) return;
  const id = `gfont-${name.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${name.replace(/\s+/g, '+')}:wght@300;400;500;600;700;800;900&display=swap`;
  document.head.appendChild(link);
}

export default function AdminEditButton() {
  const { user } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadSettings();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    applySettings(settings);
  }, [settings]);

  const applySettings = (s) => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', s.primary_color);
    root.style.setProperty('--brand-primary-hover', s.primary_hover);
    root.style.setProperty('--brand-bg', s.background_color);
    root.style.setProperty('--brand-surface', s.surface_color);
    root.style.setProperty('--brand-text', s.text_primary);
    root.style.setProperty('--brand-text-sec', s.text_secondary);
    root.style.setProperty('--brand-border', s.border_color);
    root.style.setProperty('--brand-accent', s.accent_color);
    root.style.setProperty('--brand-warning', s.warning_color);
    root.style.setProperty('--brand-danger', s.danger_color);
    root.style.setProperty('--brand-font-heading', s.font_heading);
    root.style.setProperty('--brand-font-body', s.font_body);
    root.style.setProperty('--brand-font-mono', s.font_mono);
    root.style.setProperty('--brand-font-size', `${s.font_size_base}px`);

    // Load fonts
    loadGoogleFont(s.font_heading);
    loadGoogleFont(s.font_body);
    loadGoogleFont(s.font_mono);
  };

  const loadSettings = async () => {
    try {
      const { data } = await api.get('/admin/site-settings');
      if (data && Object.keys(data).length > 0) {
        const merged = { ...DEFAULT_SETTINGS, ...data };
        setSettings(merged);
        applySettings(merged);
      }
    } catch { /* defaults */ }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/admin/site-settings', settings);
      toast.success('Settings saved!');
      setEditMode(false);
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS);
    toast.success('Reset to defaults');
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (!user || user.role !== 'admin') return null;

  return (
    <>
      <button
        data-testid="admin-edit-button"
        onClick={() => setEditMode(!editMode)}
        className="fixed top-3 right-3 z-[10000] flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-sm transition-all"
        style={{ background: editMode ? 'var(--brand-danger)' : 'var(--brand-primary)', color: '#fff' }}
      >
        {editMode ? <X size={14} /> : <Pencil size={14} />}
        {editMode ? 'Close' : 'Edit'}
      </button>

      {editMode && (
        <div
          data-testid="admin-edit-panel"
          className="fixed top-12 right-3 z-[10000] w-80 max-h-[85vh] overflow-y-auto bg-white border shadow-xl"
          style={{ borderColor: 'var(--brand-border)' }}
        >
          <div className="p-4 border-b" style={{ borderColor: 'var(--brand-border)' }}>
            <h3 className="text-sm font-bold tracking-tight flex items-center gap-2" style={{ color: 'var(--brand-text)' }}>
              <Palette size={14} /> Site Customizer
            </h3>
            <p className="text-[10px] mt-0.5" style={{ color: 'var(--brand-text-sec)' }}>
              Changes apply live. Save to persist.
            </p>
          </div>

          <div className="p-4 space-y-5">
            {/* Text Settings */}
            <Section icon={Type} title="Text">
              <SettingInput label="Brand Name" value={settings.brand_name} onChange={(v) => updateSetting('brand_name', v)} testId="edit-brand-name" />
              <SettingInput label="Tagline" value={settings.tagline} onChange={(v) => updateSetting('tagline', v)} testId="edit-tagline" />
              <SettingInput label="Hero Heading" value={settings.hero_heading} onChange={(v) => updateSetting('hero_heading', v)} testId="edit-hero-heading" />
            </Section>

            {/* Font Settings */}
            <Section icon={Type} title="Fonts">
              <div>
                <Label className="text-[10px]" style={{ color: 'var(--brand-text-sec)' }}>Heading Font</Label>
                <Select value={settings.font_heading} onValueChange={(v) => updateSetting('font_heading', v)}>
                  <SelectTrigger className="h-8 text-xs rounded-sm mt-0.5" data-testid="edit-font-heading">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FONT_OPTIONS.map(f => (
                      <SelectItem key={f.value} value={f.value} className="text-xs">{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px]" style={{ color: 'var(--brand-text-sec)' }}>Body Font</Label>
                <Select value={settings.font_body} onValueChange={(v) => updateSetting('font_body', v)}>
                  <SelectTrigger className="h-8 text-xs rounded-sm mt-0.5" data-testid="edit-font-body">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FONT_OPTIONS.map(f => (
                      <SelectItem key={f.value} value={f.value} className="text-xs">{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px]" style={{ color: 'var(--brand-text-sec)' }}>Mono Font</Label>
                <Select value={settings.font_mono} onValueChange={(v) => updateSetting('font_mono', v)}>
                  <SelectTrigger className="h-8 text-xs rounded-sm mt-0.5" data-testid="edit-font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONO_OPTIONS.map(f => (
                      <SelectItem key={f.value} value={f.value} className="text-xs">{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </Section>

            {/* Font Size */}
            <Section icon={Ruler} title="Font Size">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <Label className="text-[10px]" style={{ color: 'var(--brand-text-sec)' }}>Base Size</Label>
                  <span className="text-xs font-mono font-bold" style={{ color: 'var(--brand-primary)' }} data-testid="font-size-display">
                    {settings.font_size_base}px
                  </span>
                </div>
                <Slider
                  data-testid="edit-font-size"
                  value={[settings.font_size_base]}
                  onValueChange={([v]) => updateSetting('font_size_base', v)}
                  min={12}
                  max={22}
                  step={1}
                />
                <div className="flex justify-between text-[9px] mt-0.5" style={{ color: 'var(--brand-text-sec)' }}>
                  <span>12px</span><span>22px</span>
                </div>
              </div>
            </Section>

            {/* Color Settings */}
            <Section icon={Palette} title="Colors">
              <ColorPicker label="Primary" value={settings.primary_color} onChange={(v) => updateSetting('primary_color', v)} testId="edit-primary-color" />
              <ColorPicker label="Primary Hover" value={settings.primary_hover} onChange={(v) => updateSetting('primary_hover', v)} testId="edit-primary-hover" />
              <ColorPicker label="Background" value={settings.background_color} onChange={(v) => updateSetting('background_color', v)} testId="edit-bg-color" />
              <ColorPicker label="Surface" value={settings.surface_color} onChange={(v) => updateSetting('surface_color', v)} testId="edit-surface-color" />
              <ColorPicker label="Text" value={settings.text_primary} onChange={(v) => updateSetting('text_primary', v)} testId="edit-text-primary" />
              <ColorPicker label="Text Secondary" value={settings.text_secondary} onChange={(v) => updateSetting('text_secondary', v)} testId="edit-text-secondary" />
              <ColorPicker label="Border" value={settings.border_color} onChange={(v) => updateSetting('border_color', v)} testId="edit-border-color" />
              <ColorPicker label="Accent" value={settings.accent_color} onChange={(v) => updateSetting('accent_color', v)} testId="edit-accent-color" />
              <ColorPicker label="Warning" value={settings.warning_color} onChange={(v) => updateSetting('warning_color', v)} testId="edit-warning-color" />
              <ColorPicker label="Danger" value={settings.danger_color} onChange={(v) => updateSetting('danger_color', v)} testId="edit-danger-color" />
            </Section>
          </div>

          <div className="p-4 border-t flex gap-2" style={{ borderColor: 'var(--brand-border)' }}>
            <button
              data-testid="edit-reset-btn"
              onClick={handleReset}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium border bg-white transition-colors"
              style={{ borderColor: 'var(--brand-border)', color: 'var(--brand-text-sec)' }}
            >
              <RotateCcw size={12} /> Reset
            </button>
            <button
              data-testid="edit-save-btn"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium text-white transition-colors disabled:opacity-50"
              style={{ background: 'var(--brand-primary)' }}
            >
              <Save size={12} /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <p className="text-[10px] font-bold tracking-[0.15em] uppercase flex items-center gap-1 mb-2" style={{ color: 'var(--brand-text-sec)' }}>
        <Icon size={12} /> {title}
      </p>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function SettingInput({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[10px]" style={{ color: 'var(--brand-text-sec)' }}>{label}</Label>
      <Input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="h-7 text-xs rounded-sm" style={{ borderColor: 'var(--brand-border)' }} />
    </div>
  );
}

function ColorPicker({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between">
      <Label className="text-[10px]" style={{ color: 'var(--brand-text-sec)' }}>{label}</Label>
      <div className="flex items-center gap-1.5">
        <input data-testid={testId} type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-6 h-6 border cursor-pointer p-0" style={{ borderColor: 'var(--brand-border)' }} />
        <span className="text-[10px] font-mono w-14" style={{ color: 'var(--brand-text-sec)' }}>{value}</span>
      </div>
    </div>
  );
}
